import React from 'react';
import { Box, Paper, Typography, Divider, Button, Chip } from '@mui/material';
import { formatCurrency } from '../utils/pricing';
import { useCart } from '../context/CartContext';

interface OrderSummaryProps {
  onCheckout?: () => void;
  checkoutButtonText?: string;
  showCheckoutButton?: boolean;
  showFullBreakdown?: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  onCheckout,
  checkoutButtonText = 'Proceed to Checkout',
  showCheckoutButton = true,
  showFullBreakdown = true
}) => {
  const { 
    items, 
    itemCount, 
    subtotal, 
    gst, 
    pst,
    deposit, 
    ecoFee, 
    deliveryFee,
    total 
  } = useCart();

  // Free shipping threshold message
  const freeShippingThreshold = 100;
  const amountToFreeShipping = freeShippingThreshold - subtotal;
  const qualifiesForFreeShipping = subtotal >= freeShippingThreshold;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" component="h2" gutterBottom fontWeight={600}>
        Order Summary
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">
            Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {formatCurrency(subtotal)}
          </Typography>
        </Box>
        
        {showFullBreakdown && (
          <>
            {gst > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  GST (5%)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(gst)}
                </Typography>
              </Box>
            )}
            
            {pst > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  PST (7%)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(pst)}
                </Typography>
              </Box>
            )}
            
            {deposit > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Bottle Deposit
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(deposit)}
                </Typography>
              </Box>
            )}
            
            {ecoFee > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Eco Fee
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(ecoFee)}
                </Typography>
              </Box>
            )}
          </>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Delivery
          </Typography>
          <Typography 
            variant="body2" 
            color={qualifiesForFreeShipping ? "success.main" : "text.secondary"} 
            fontWeight={500}
          >
            {qualifiesForFreeShipping ? 'Free' : formatCurrency(deliveryFee)}
          </Typography>
        </Box>
        
        {!qualifiesForFreeShipping && (
          <Box sx={{ mt: 1, mb: 2, textAlign: 'center', color: 'darkgreen', fontSize: '.8rem' }}>
            {`Add ${formatCurrency(amountToFreeShipping)} more for free delivery`}
          </Box>
        )}
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Total
        </Typography>
        <Typography variant="h6" fontWeight={600}>
          {formatCurrency(total)}
        </Typography>
      </Box>
      
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3, textAlign: 'right' }}>
        All taxes & fees included
      </Typography>
      
      {items.length > 0 && showCheckoutButton && (
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={onCheckout}
          sx={{ py: 1.5 }}
        >
          {checkoutButtonText}
        </Button>
      )}
    </Paper>
  );
};

export default OrderSummary; 